var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useState, useMemo, useReducer, useCallback, useRef, } from "react";
import { v4 as uuid } from "uuid";
import { ConnectionStatus, } from "@2stand-events/chat-client";
import { IsChannelListProvider, ExternalUsersProvider, useClient, useConnectionStatus, useTranslation, } from "../../hooks";
import S from "./ChannelList.styled";
import SearchIcon from "../../assets/icons/SearchIcon";
import EditIcon from "../../assets/icons/EditIcon";
import LeftArrowIcon from "../../assets/icons/LeftArrow";
import RightArrowIcon from "../../assets/icons/RightArrow";
import CloseIcon from "../../assets/icons/CloseIcon";
import User from "../User/User";
import { channelListReducer, channelListInitialState } from "./reducer";
import ChannelListSection from "./ChannelListSection";
import ChannelPanel from "./ChannelPanel";
import SettingsIcon from "../../assets/icons/SettingsIcon";
import isSubstring from "../utils";
import { useTheme } from "@emotion/react";
const ChannelList = ({ users, children, }) => {
    const client = useClient();
    const strings = useTranslation();
    const status = useConnectionStatus();
    const theme = useTheme();
    const [{ channels, selectedChannel, isLoading, isUserListOpen, isOpenChannelMembers, isCollapsable, }, dispatch,] = useReducer(channelListReducer, channelListInitialState);
    const [queryString, setQueryString] = useState("");
    const [lastMessage, setLastMessage] = useState(null);
    const headerButtonRef = useRef(null);
    const settingsRef = useRef(null);
    const filteredChannels = useMemo(() => {
        return [
            ...channels.filter((e) => {
                var _a;
                return (isSubstring(e.name, queryString) ||
                    (e.channelType === "private" &&
                        ((_a = e.data.members) !== null && _a !== void 0 ? _a : [])
                            .map((id) => users.get(id))
                            .some((m) => { var _a; return isSubstring((_a = m === null || m === void 0 ? void 0 : m.name) !== null && _a !== void 0 ? _a : "", queryString); })));
            }),
        ].sort((a, b) => {
            if (!a.lastMessage && !b.lastMessage)
                return 0;
            if (!a.lastMessage)
                return 1;
            if (!b.lastMessage)
                return -1;
            const aDate = new Date(a.lastMessage.date);
            const bDate = new Date(b.lastMessage.date);
            if (aDate > bDate)
                return -1;
            if (bDate > aDate)
                return 1;
            return 0;
        });
    }, [channels, queryString, lastMessage]);
    const unreadMessages = useMemo(() => filteredChannels.filter((c) => c.unreadMessagesCount > 0), [filteredChannels]);
    const directMessages = useMemo(() => filteredChannels.filter((c) => c.channelType === "private" &&
        c.unreadMessagesCount === 0 &&
        c.lastMessage), [filteredChannels]);
    const groupMessages = useMemo(() => filteredChannels.filter((c) => c.channelType === "restricted" && c.unreadMessagesCount === 0), [filteredChannels]);
    useEffect(() => {
        function fetchList() {
            return __awaiter(this, void 0, void 0, function* () {
                if (status === ConnectionStatus.Connected) {
                    yield client.queryChannels();
                    const urlParams = new URLSearchParams(window.location.search);
                    const privateMemberId = urlParams.get("pch");
                    const groupChatId = urlParams.get("gch");
                    if (privateMemberId && privateMemberId !== client.user.id) {
                        const privateChannel = client.channel("private", "", {
                            name: "Private Channel",
                            members: [client.user.id, privateMemberId],
                        });
                        privateChannel.watch().then(() => {
                            dispatch({ type: "selectChannel", channel: privateChannel });
                        });
                    }
                    else if (groupChatId) {
                        const groupChannel = client.channel("restricted", groupChatId, {
                            members: [client.user.id],
                        });
                        groupChannel.watch().then(() => {
                            dispatch({ type: "selectChannel", channel: groupChannel });
                        });
                    }
                }
            });
        }
        fetchList();
    }, [client, status]);
    useEffect(() => {
        const unsubscribeFunctions = [
            client.on("queriedList", (event) => {
                const chs = event.channels;
                dispatch({
                    type: "initializeChannels",
                    channels: chs,
                });
            }),
            client.on("newConnectedChannel", (event) => {
                const ch = event.channel;
                dispatch({ type: "addChannel", channel: ch });
            }),
            client.on("newMessage", (event) => {
                setLastMessage(event.message);
            }),
        ];
        return () => {
            unsubscribeFunctions.forEach((f) => f.unsubscribe());
        };
    }, [client]);
    const handleChannelSelect = useCallback((channel) => {
        dispatch({ type: "selectChannel", channel });
        dispatch({ type: "closeUserList" });
        dispatch({ type: "openCollapse" });
    }, []);
    const handleNewChat = useCallback((ids, groupTitle) => {
        let newChannel;
        if (ids.size <= 1) {
            newChannel = client.channel("private", "", {
                name: "Private Channel",
                members: [client.user.id, ...Array.from(ids)],
            });
        }
        else {
            const id = `ug-${uuid()}`;
            newChannel = client.channel("restricted", id, {
                name: groupTitle !== null && groupTitle !== void 0 ? groupTitle : "Group chat",
                members: [client.user.id, ...Array.from(ids)],
            });
        }
        newChannel.watch().then(() => {
            dispatch({ type: "selectChannel", channel: newChannel });
        });
        dispatch({ type: "closeUserList" });
    }, [client]);
    const onKeyPress = (event) => {
        if (event.key === "Enter") {
            dispatch({ type: "toggleMemberList" });
        }
        if (event.key === "Escape") {
            dispatch({ type: "closeChannelMembers" });
        }
    };
    const escapeChannelMembers = () => {
        var _a;
        dispatch({ type: "closeChannelMembers" });
        (_a = settingsRef === null || settingsRef === void 0 ? void 0 : settingsRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    const escapeUserList = () => {
        var _a;
        dispatch({ type: "closeUserList" });
        (_a = headerButtonRef === null || headerButtonRef === void 0 ? void 0 : headerButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    return (React.createElement(IsChannelListProvider, { value: true },
        React.createElement(ExternalUsersProvider, { users: users },
            React.createElement(S.Wrapper, null,
                React.createElement(S.LeftArrowIconDiv, { onClick: () => dispatch({ type: "closeCollapse" }) },
                    React.createElement(S.LeftArrowIcon, { isCollapsable: isCollapsable },
                        React.createElement(LeftArrowIcon, null))),
                React.createElement(S.RightArrowIconDiv, { onClick: () => dispatch({ type: "openCollapse" }) },
                    React.createElement(S.RightArrowIcon, { isCollapsable: isCollapsable },
                        React.createElement(RightArrowIcon, null))),
                React.createElement(S.ListWrapper, { isCollapsable: isCollapsable },
                    React.createElement(S.ChannelListHeader, null,
                        React.createElement(User, { user: client.user }),
                        React.createElement(S.HeaderButton, { type: "button", onClick: () => {
                                dispatch({ type: "toggleUserList" });
                            }, ref: headerButtonRef }, isUserListOpen ? (React.createElement(React.Fragment, null,
                            React.createElement(CloseIcon, { fill: "currentColor" }),
                            "Close")) : (React.createElement(React.Fragment, null,
                            React.createElement(EditIcon, { fill: "currentColor" }),
                            "New Chat")))),
                    React.createElement(S.ChannelSearchContainer, null,
                        React.createElement(S.ChannelSearchInputWrapper, null,
                            React.createElement(S.ChannelSearchInputIcon, null,
                                React.createElement(SearchIcon, { fill: "white" }),
                                React.createElement(S.ChannelSearchInputText, { type: "search", placeholder: strings.searchConversations, "aria-label": strings.searchConversations, onChange: (e) => {
                                        setQueryString(e.target.value);
                                    } })))),
                    React.createElement(S.OuterListWrapper, null,
                        React.createElement(S.List, { onClick: () => dispatch({ type: "closeChannelMembers" }) },
                            React.createElement(ChannelListSection, { header: strings.headerUM, channels: unreadMessages, selectedChannel: selectedChannel, handleSelect: handleChannelSelect }),
                            React.createElement(ChannelListSection, { header: strings.headerDM, channels: directMessages, selectedChannel: selectedChannel, handleSelect: handleChannelSelect }),
                            React.createElement(ChannelListSection, { header: strings.headerGM, channels: groupMessages, selectedChannel: selectedChannel, handleSelect: handleChannelSelect }))),
                    React.createElement(S.Padder, null)),
                (selectedChannel === null || selectedChannel === void 0 ? void 0 : selectedChannel.channelType) === "restricted" && !isUserListOpen && (React.createElement(S.SettingsIconDiv, { onClick: () => dispatch({ type: "toggleMemberList" }), isCollapsable: isCollapsable, tabIndex: 0, onKeyDown: onKeyPress, ref: settingsRef },
                    React.createElement(SettingsIcon, { fill: theme.color.backgroundIcon }))),
                React.createElement(S.PannelWrapper, { isCollapsable: isCollapsable },
                    React.createElement(ChannelPanel, { isUserListOpen: isUserListOpen, isLoading: isLoading, selectedChannel: selectedChannel, handleNewChat: handleNewChat, isOpenChannelMembers: isOpenChannelMembers, handleLeaveGroup: () => {
                            dispatch({ type: "leaveGroup" });
                        }, handleSendMessage: (newChannel) => dispatch({ type: "selectChannel", channel: newChannel }), handleEscapeEvent: () => {
                            if (isOpenChannelMembers) {
                                escapeChannelMembers();
                            }
                            if (isUserListOpen) {
                                escapeUserList();
                            }
                        } }, children))))));
};
export default ChannelList;
//# sourceMappingURL=ChannelList.js.map